import type {Dispatch, SetStateAction} from 'react';
import {useQuery} from '@shopify/react-graphql';
import {useI18n} from '@shopify/react-i18n';
import type {Image} from '@web-utilities/types';

import insertImageModalQuery, {
  type InsertImageModalQueryData,
} from '../graphql/InsertImageModalQuery.graphql';
import {DEFAULT_IMAGES, DEFAULT_QUERY} from '../constants';

export const useInsertImageModalQuery = (
  open: boolean,
  handleToast: (content: string, error?: boolean) => void,
  setActiveImage: Dispatch<SetStateAction<Image | null>>,
) => {
  const [i18n] = useI18n();

  const {data, loading, error, refetch, variables} =
    useQuery<InsertImageModalQueryData>(insertImageModalQuery, {
      variables: DEFAULT_QUERY,
      skip: !open,
    });

  const handlePaginateProductImagesPrevious = () => {
    if (!data || loading || error) {
      return;
    }
    const edges = data.shop.productImages.edges;
    const cursor = edges[0].cursor;
    try {
      refetch({
        ...variables,
        productImagesFirst: null,
        productImagesLast: DEFAULT_IMAGES,
        productCursorAfter: null,
        productCursorBefore: cursor,
      });
      setActiveImage(null);
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  const handlePaginateProductImagesNext = () => {
    if (!data || loading || error) {
      return;
    }
    const edges = data.shop.productImages.edges;
    const cursor = edges[edges.length - 1].cursor;
    try {
      refetch({
        ...variables,
        productImagesFirst: DEFAULT_IMAGES,
        productImagesLast: null,
        productCursorAfter: cursor,
        productCursorBefore: null,
      });
      setActiveImage(null);
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  const handlePaginateUploadedImagesNext = () => {
    if (!data || loading || error) {
      return;
    }
    const images = data.shop.uploadedImages.edges;
    const cursor = images[images.length - 1].cursor;
    try {
      refetch({
        ...variables,
        uploadedImagesFirst: DEFAULT_IMAGES,
        uploadedImagesLast: null,
        uploadedCursorAfter: cursor,
        uploadedCursorBefore: null,
      });
      setActiveImage(null);
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  const handlePaginateUploadedImagesPrevious = () => {
    if (!data || loading || error) {
      return;
    }
    const images = data.shop.uploadedImages.edges;
    const cursor = images[0].cursor;
    try {
      refetch({
        ...variables,
        uploadedImagesFirst: null,
        uploadedImagesLast: DEFAULT_IMAGES,
        uploadedCursorAfter: null,
        uploadedCursorBefore: cursor,
      });
      setActiveImage(null);
    } catch (err) {
      handleToast(i18n.translate('Common.serverError'), true);
    }
  };

  return {
    data,
    loading,
    error,
    refetch,
    variables,
    handlePaginateProductImagesPrevious,
    handlePaginateProductImagesNext,
    handlePaginateUploadedImagesNext,
    handlePaginateUploadedImagesPrevious,
  };
};
